import { useCallback } from 'react'
import {
  ZOOM_STEP_PERCENT,
  ZOOM_MIN_PERCENT,
  ZOOM_MAX_PERCENT_RASTER,
  ZOOM_MAX_PERCENT_VECTOR,
  clampPercent,
  snapPercentToStep,
  fitZoomPercent,
  percentToFitScale,
} from '@/constants/zoom'

/**
 * useZoom — the single zoom controller shared by every zoomable surface (FS-562).
 *
 * Each surface stores its scale in its own units (tldraw `camera.z`, a d3
 * transform `k`, a cytoscape zoom, a CSS scale, a PDF render scale). This hook
 * does NOT own that state — the surface does — it only:
 *   - derives the canonical display **percent** from the current scale, and
 *   - computes the next scale for +/- / type-in, **clamped in percent space**,
 *     and hands it back via `onApply`.
 *
 * Clamping the percentage the user reads (not the raw scale) is what fixes both
 * reported bugs by construction: the readout always tracks the real scale (so
 * wheel/pinch zoom-out shows a falling number), and zoom-in can't exceed
 * `maxPercent` regardless of how small the fit baseline is (no more ~3000%).
 *
 * Three models cover every surface:
 *   - `absolute`      100% == scale 1.0          (image, datasheet, region-select)
 *   - `fit-baseline`  100% == the fit-to-view scale (P&ID, Knowledge Graph, board)
 *   - `percent`       100% == a fit-to-width scale  (PDF viewer)
 */
export type ZoomModel = 'absolute' | 'fit-baseline' | 'percent'

export interface UseZoomOptions {
  model: ZoomModel
  /** Current native scale. Pass it reactively so `percent` re-derives on change. */
  scale: number
  /**
   * Native scale that reads as 100%. `1` for `absolute`; the fit scale for
   * `fit-baseline`; the fit-to-width scale for `percent`. Defaults to 1.
   */
  baseline?: number
  /** Display floor (%). Defaults to `ZOOM_MIN_PERCENT`. */
  minPercent?: number
  /**
   * Display ceiling (%). Defaults to 200 for `fit-baseline` (vector cap) and 400
   * otherwise (raster cap).
   */
  maxPercent?: number
  /** Push the next native scale to the surface (tldraw/d3/cytoscape/css/pdf). */
  onApply: (nextScale: number) => void
}

export interface UseZoomApi {
  /** Canonical readout — the actual zoom level, rounded. */
  percent: number
  zoomIn: () => void
  zoomOut: () => void
  /** Type-in entry point: snaps to the step grid, clamps, then applies. */
  setPercent: (percent: number) => void
  /** Back to 100% (the baseline). */
  reset: () => void
  canZoomIn: boolean
  canZoomOut: boolean
}

/** Native scale → display percentage, per model. Pure; exported for tests. */
export function scaleToPercent(
  model: ZoomModel,
  scale: number,
  baseline: number
): number {
  const base = baseline || 1
  if (model === 'fit-baseline') return fitZoomPercent(scale, base)
  return Math.round((scale / base) * 100)
}

/** Display percentage → native scale, per model. Inverse of `scaleToPercent`. */
export function percentToScale(
  model: ZoomModel,
  percent: number,
  baseline: number
): number {
  const base = baseline || 1
  if (model === 'fit-baseline') return percentToFitScale(percent, base)
  return (percent / 100) * base
}

/**
 * The next display percentage after one +/- click. Steps by `ZOOM_STEP_PERCENT`
 * (1pp) from the current value; if the current value is off that grid (e.g. after a wheel
 * zoom), the first click snaps toward the grid in the step direction so the
 * sequence stays clean and zoom-in/zoom-out remain inverses on-grid.
 */
export function nextStepPercent(
  currentPercent: number,
  direction: 1 | -1
): number {
  // Kill float noise before comparing against the grid. Surfaces that derive
  // the percent from a scale ratio hand us values like 89.99999999999999
  // (e.g. (0.9 * base) / base * 100); treating that as "below 90" made the
  // first + click snap to 90 — the level the user was already on, which the
  // caller then no-ops — wedging zoom-in below 100% (FS-562 follow-up). A
  // 1e-6 grid is far finer than any real zoom distinction and leaves genuine
  // off-grid values (wheel zoom, e.g. 137.4) untouched.
  const current = Math.round(currentPercent * 1e6) / 1e6
  const snapped = snapPercentToStep(current)
  if (direction > 0) {
    return snapped <= current ? snapped + ZOOM_STEP_PERCENT : snapped
  }
  return snapped >= current ? snapped - ZOOM_STEP_PERCENT : snapped
}

export function defaultMaxPercent(model: ZoomModel): number {
  return model === 'fit-baseline'
    ? ZOOM_MAX_PERCENT_VECTOR
    : ZOOM_MAX_PERCENT_RASTER
}

export function useZoom({
  model,
  scale,
  baseline = 1,
  minPercent = ZOOM_MIN_PERCENT,
  maxPercent,
  onApply,
}: UseZoomOptions): UseZoomApi {
  const max = maxPercent ?? defaultMaxPercent(model)
  const min = minPercent
  const percent = scaleToPercent(model, scale, baseline)

  const applyPercent = useCallback(
    (target: number) => {
      const clamped = clampPercent(target, min, max)
      const current = scaleToPercent(model, scale, baseline)
      if (clamped === current) return
      onApply(percentToScale(model, clamped, baseline))
    },
    [model, scale, baseline, min, max, onApply]
  )

  const zoomIn = useCallback(
    () =>
      applyPercent(nextStepPercent(scaleToPercent(model, scale, baseline), 1)),
    [applyPercent, model, scale, baseline]
  )
  const zoomOut = useCallback(
    () =>
      applyPercent(nextStepPercent(scaleToPercent(model, scale, baseline), -1)),
    [applyPercent, model, scale, baseline]
  )
  /**
   * Type-in entry. Rounds to a WHOLE PERCENT, not to the +/- step grid.
   *
   * These are two different controls and they were sharing one grid. The
   * buttons are deliberately coarse (`ZOOM_STEP`, 10pp) so a click is visible;
   * snapping typed input to the same ladder meant 117 silently became 120,
   * which takes away the only exact control the surface has. Whole-percent
   * rounding still removes the fractions a wheel zoom produces.
   */
  const setPercent = useCallback(
    (p: number) => applyPercent(Math.round(p)),
    [applyPercent]
  )
  const reset = useCallback(() => applyPercent(100), [applyPercent])

  return {
    percent,
    zoomIn,
    zoomOut,
    setPercent,
    reset,
    canZoomIn: percent < max,
    canZoomOut: percent > min,
  }
}
